import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { UserCtx } from '../../../../contexts/UserCtx';
import { colors, mixins, zIndex } from '../../../css';
import NavLink from '../common/NavLink';

const expandedWidth = mixins.pxToRem(200);
const shrinkWidth = mixins.pxToRem(80);

const stretchOpen = css`
  ${keyframes`
  0%{
    width: ${shrinkWidth};
  }
  100% {
    width: ${expandedWidth};
  }
  `}
`;

const stretchClosed = css`
  ${keyframes`
  0%{
    width: ${expandedWidth};
  }
  100% {
    width: ${shrinkWidth};
  }
  `}
`;

const labelOpen = css`
  ${keyframes`
  0%{
    width: 0;
  }
  100% {
    width: calc(100% - ${shrinkWidth});
  }
  `}
`;

const labelClosed = css`
  ${keyframes`
  0%{
    width: calc(100% - ${shrinkWidth});
  }
  100% {
    width: 0;
  }
  `}
`;

export const SideNavContainer = styled.div`
  z-index: ${zIndex.navigation.side};
  width: ${shrinkWidth};
  ${({ theme }) => `background-color: ${theme.darkTheme}`};

  &.stretch-enter {
    width: ${shrinkWidth};
  }
  &.stretch-enter-active {
    animation: ${stretchOpen} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  }
  &.stretch-enter-done {
    width: ${expandedWidth};
  }
  &.stretch-exit {
    width: ${expandedWidth};
  }
  &.stretch-exit-active {
    animation: ${stretchClosed} 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53) both;
  }
  &.stretch-exit-done {
    width: ${shrinkWidth};
  }
`;

export const SideNavContents = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  height: calc(100% - 80px);
  overflow-y: auto;
  ${({ theme }) => `background-color: ${theme.darkTheme}`};
`;

export const HighlightedNav = styled.div`
  display: flex;
  align-items: center;
  padding: ${mixins.pxToRem(15)} ${mixins.pxToRem(25)};
  width: 100%;
  height: 100%;
  min-height: ${mixins.pxToRem(54)};
  ${({ theme }) => `background-color: ${theme.darkTheme}`};

  &.curRoute {
    ${({ theme }) => `background-color: ${theme.mainTheme}`};
  }

  &:hover {
    ${({ theme }) => `background-color: ${theme.darkerTheme}`};
    &.curRoute {
      ${({ theme }) => `background-color: ${theme.midTheme}`};
    }
  }

  figure {
    cursor: pointer;
  }
`;

export const RouteRect = styled.div`
  position: absolute;
  height: ${mixins.pxToRem(40)};
  width: ${mixins.pxToRem(7)};
  ${({ theme }) => `background-color: ${theme.lightTheme}`};

  &.expand {
    top: ${mixins.pxToRem(7)};
  }
`;

const SideNavLink = css`
  width: ${shrinkWidth};
  overflow: hidden;
  height: ${mixins.pxToRem(54)};

  .expandMenu {
    width: ${shrinkWidth};
  }

  .label {
    position: absolute;
    left: ${mixins.pxToRem(80)};
    overflow: hidden;
    white-space: nowrap;
    width: 0;
  }

  .arrow {
    transform: rotate(-90deg);
    margin-right: 0;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    &.stretch {
      transform: rotate(0);
      margin-right: ${mixins.pxToRem(10)};
    }

    &.expand {
      transform: rotate(-180deg);
      margin-right: ${mixins.pxToRem(10)};
    }
  }

  &.stretch-enter {
    width: ${shrinkWidth};
    .expandMenu {
      width: ${shrinkWidth};
    }
    .label {
      width: 0;
    }
  }
  &.stretch-enter-active {
    animation: ${stretchOpen} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
    .expandMenu {
      animation: ${stretchOpen} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
    }
    .label {
      animation: ${labelOpen} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
    }
  }
  &.stretch-enter-done {
    width: ${expandedWidth};
    .expandMenu {
      width: ${expandedWidth};
    }
    .label {
      width: calc(100% - ${shrinkWidth});
    }
  }
  &.stretch-exit {
    width: ${expandedWidth};
    .expandMenu {
      width: ${expandedWidth};
    }
    .label {
      width: calc(100% - ${shrinkWidth});
    }
  }
  &.stretch-exit-active {
    animation: ${stretchClosed} 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53) both;
    .expandMenu {
      animation: ${stretchClosed} 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53) both;
    }
    .label {
      animation: ${labelClosed} 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53) both;
    }
  }
  &.stretch-exit-done {
    width: ${shrinkWidth};
    .expandMenu {
      width: ${shrinkWidth};
    }
    .label {
      width: 0;
    }
  }
`;

const disabledStyle = css`
  background-color: ${colors.colorGrey04};
  &:hover {
    cursor: auto;
    figure {
      // Undo the earlier important (todo: avoid important)
      cursor: auto !important;
    }
  }
`;

export const StyledNavLink = styled(NavLink)`
  ${({ theme }) => `color: ${theme.lightText}`};
  ${({ theme }) => `background-color: ${theme.darkTheme}`};
  min-height: ${mixins.pxToRem(54)};
  &:hover {
    ${({ theme }) => `color: ${theme.lightTheme}`};
  }
  ${SideNavLink};
  ${({ disabled }) => disabled && disabledStyle};
`;

const noClick = (e) => e.preventDefault();

export function SecuredNavLink(props) {
  const { reqPermission, children } = props;
  const passThroughProps = { ...props };
  if (reqPermission !== undefined) {
    const userCtx = React.useContext(UserCtx);
    const denyReason = userCtx.denyReason(reqPermission);
    if (denyReason !== undefined) {
      passThroughProps.title += ' (unauthorized)';
      passThroughProps.onClick = noClick;
      passThroughProps.href = undefined;
      passThroughProps.disabled = true;
    }
  }
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <StyledNavLink {...passThroughProps}>{children}</StyledNavLink>;
}
SecuredNavLink.propTypes = {
  children: PropTypes.node.isRequired,
  reqPermission: PropTypes.string
};
SecuredNavLink.defaultProps = {
  reqPermission: undefined
};

export const HelpSupport = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-grow: 1;
  flex-direction: column;
  padding: ${mixins.pxToRem(10)} 0;
  min-height: ${mixins.pxToRem(54)};
`;
